import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, ResolveFn} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {catchError, map, of, switchMap} from 'rxjs';
import {ServiceProviderHub} from '@generated-backend-types';
import {EventOverviewSnapshotService} from '../../services/event-overview-snapshot/event-overview-snapshot.service';
import {EventInquiriesService} from '../../services/event-inquiries/event-inquiries.service';
import EventInquiry = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.Inquiry.EventInquiry;
import EventOverviewSnapshot = ServiceProviderHub.App.Http.Resources.ServiceProviderHub.Eventmanagement.Event.EventOverviewSnapshot.EventOverviewSnapshot;

const eventId = (route: ActivatedRouteSnapshot): string => {
  let current: ActivatedRouteSnapshot | null = route;
  while (current && current.routeConfig?.path !== ':id') {
    current = current.parent;
  }
  return current?.params['id'] ?? route.params['id'];
};

export const resolveEventOverviewSnapshot: ResolveFn<EventOverviewSnapshot | null> = route => {
  const service = inject(EventOverviewSnapshotService);
  service.forEvent(eventId(route));
  return service.get(route.params['snapshotId']).pipe(
    map(response => response.data),
    catchError(() => of(null)),
  );
};

// The snapshot inquiries endpoint returns table rows without variants, contacts etc., so the newest row is reloaded in full.
export const resolveEventOverviewSnapshotInquiry: ResolveFn<EventInquiry | null> = route => {
  const service = inject(EventOverviewSnapshotService);
  const inquiries = inject(EventInquiriesService);
  service.forEvent(eventId(route));
  inquiries.forEvent(eventId(route));
  return service.inquiriesForSnapshot(route.params['snapshotId']).pipe(
    map(rows => rows.reduce<EventInquiry | null>(
      (newest, inquiry) => !newest || inquiry.created_at > newest.created_at ? inquiry : newest,
      null,
    )),
    switchMap(newest => newest ? inquiries.get(newest.id).pipe(map(response => response.data)) : of(null)),
    catchError(() => of(null)),
  );
};

export const resolveEventOverviewSnapshotBreadcrumb: ResolveFn<string> = route => {
  const service = inject(EventOverviewSnapshotService);
  const translate = inject(TranslateService);
  service.forEvent(eventId(route));
  return service.get(route.params['snapshotId']).pipe(
    map(response => translate.instant('events.detail.event_overview_snapshot.breadcrumb', {version: response.data.version_number})),
    catchError(() => of('')),
  );
};
